
import EventTreeMxGraphDecoder from 'hcl-web-editor/app/mxGraph/HCLTreeMxGraphDecoder/EventTreeMxGraphDecoder';
import FunctionalEventVertex from 'hcl-web-editor/app/mxGraph/HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertex';
import FunctionalEventVertexValue from 'hcl-web-editor/app/mxGraph/HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertexValue';
import EventTreeMxGraph, {
  BranchJSON,
  EventTreeMxGraphJSON
} from 'hcl-web-editor/app/mxGraph/HCLTreeMxGraph/EventTreeMxGraph';
import ReferenceTypes
  from 'hcl-web-editor/app/mxGraph/HCLTreeNode/HCLTreeVertexValue/ReferenceTypes';
import Outcome, { OutcomeJSON } from 'hcl-web-editor/app/mxGraph/HCLTreeNode/HCLTreeVertexValue/Outcome';
import Label from 'hcl-web-editor/app/mxGraph/HCLTreeNode/HCLTreeVertexValue/Label';
import EventTreeVertex from 'hcl-web-editor/app/mxGraph/HCLTreeNode/EventTreeNode/EventTreeVertex';
import ProxyTypes from 'hcl-web-editor/app/mxGraph/HCLTreeNode/HCLTreeVertexValue/ProxyTypes';
import Instruction from 'hcl-web-editor/app/mxGraph/HCLTreeNode/HCLTreeVertexValue/Instruction';
import FunctionalEventPortValue from 'hcl-web-editor/app/mxGraph/HCLTreeNode/EventTreeNode/FunctionalEventNode/Port/FunctionalEventPortValue';
import PhoenixFlowchartMxGraph from './PhoenixFlowchartMxGraph';
import BranchPointVertex from '../HCLTreeNode/PhoenixFlowchartNode/BranchPointVertex';
import DecisionPointVertex from '../HCLTreeNode/PhoenixFlowchartNode/DecisionPointVertex';
import BranchPointEdge from "../HCLTreeNode/PhoenixFlowchartNode/BranchPointEdge";
import DecisionPointEdge from "../HCLTreeNode/PhoenixFlowchartNode/DecisionPointEdge";

/**
 * Inherits from {@link HCLTreeMxGraphDecoder} and implements codecs for {@link EventTreeMxGraph}
 */
class PhoenixFlowchartMxGraphDecoder extends EventTreeMxGraphDecoder {

  protected functionalEvents: {[index: string]: FunctionalEventVertex | DecisionPointVertex | BranchPointVertex} = {};

  /**
   * @param {PhoenixFlowchartMxGraph} graph
   * @param {EventTreeMxGraphJSON} json
   */
  constructor(graph: PhoenixFlowchartMxGraph, json: EventTreeMxGraphJSON) {
    super(graph, json);
  }

  public createFunctionalEvents(): void {
    const json = this.getJSON() as EventTreeMxGraphJSON;
    Object.entries(json[ReferenceTypes.FUNCTIONAL_EVENTS]).forEach((e) => {
      let value = new FunctionalEventVertexValue(new Label(e[0]), null, this.reverseBranchKeyMap[e[0]]);
      // @ts-ignore
      if (e[1].label && Object.keys(e[1].label).length) value = new FunctionalEventVertexValue(Label.build(e[1].label), null, this.reverseBranchKeyMap[e[0]]);
      if (e[0].includes('DP_') || value.getLabel().getName().includes('DP_')) {
        this.functionalEvents[e[0]] = this.addVertex(new DecisionPointVertex(e[0], value)) as DecisionPointVertex;
      } else if (e[0].includes('BP_') || value.getLabel().getName().includes('BP_')) {
        this.functionalEvents[e[0]] = this.addVertex(new BranchPointVertex(e[0], value)) as BranchPointVertex;
      } else {
        this.functionalEvents[e[0]] = this.addVertex(new FunctionalEventVertex(e[0], value)) as FunctionalEventVertex;
      }
      this.functionalEvents[e[0]].visited = false;
    });
  }

  /**
   * @param {BranchJSON | OutcomeJSON} outcome
   */
  protected getVertexFromReference(outcome: BranchJSON | OutcomeJSON): EventTreeVertex {
    // proxyTypes can be of type Fork and EndState
    switch (outcome._proxy) {
      case ProxyTypes.FORK:
        const branchJSON = outcome as BranchJSON;
        const forkEventVertex = this.functionalEvents[branchJSON[ReferenceTypes.FUNCTIONAL_EVENT]];
        if (forkEventVertex.visited) {
          return forkEventVertex;
        }

        forkEventVertex.visited = true;

        if (branchJSON[ReferenceTypes.PATHS].failure.instructions) {
          (forkEventVertex.getValue() as FunctionalEventVertexValue).setInstructions([new Instruction(branchJSON[ReferenceTypes.PATHS].failure.instructions[0])]);
        }

        if (forkEventVertex.successPort) {
          (forkEventVertex.successPort.getValue() as FunctionalEventPortValue).setOutcome(Outcome.build(branchJSON[ReferenceTypes.PATHS].success.outcome));
        }

        if (forkEventVertex.failurePort) {
          (forkEventVertex.failurePort.getValue() as FunctionalEventPortValue).setOutcome(Outcome.build(branchJSON[ReferenceTypes.PATHS].failure.outcome));
        }

        const paths = branchJSON[ReferenceTypes.PATHS];
        const { success, failure } = paths;

        //for success path
        if (success && success.outcome) {
          const successVertex = this.getVertexFromReference(paths.success.outcome);
          if (successVertex) {
            if (forkEventVertex instanceof DecisionPointVertex) {
              this.addEdge(new DecisionPointEdge('yes'), forkEventVertex, successVertex);
            }
            else if (forkEventVertex instanceof BranchPointVertex) {
              this.addEdge(new BranchPointEdge('success'), forkEventVertex, successVertex);
            }
          }
        }

        // for failure path
        if (failure && failure.outcome) {
          const failureVertex = this.getVertexFromReference(branchJSON[ReferenceTypes.PATHS].failure.outcome);
          if (failureVertex) {
            if (forkEventVertex instanceof DecisionPointVertex) {
              this.addEdge(new DecisionPointEdge('no'), forkEventVertex, failureVertex);
            }
            else if (forkEventVertex instanceof BranchPointVertex) {
              this.addEdge(new BranchPointEdge('failure'), forkEventVertex, failureVertex);
            }
          }
        }

        return forkEventVertex;

      case ProxyTypes.END_STATE:
        const outcomeJSON = outcome as OutcomeJSON;
        switch (outcomeJSON.reference_type) {
          case ReferenceTypes.SEQUENCES:
            return this.sequences[outcomeJSON.name];
          case ReferenceTypes.BRANCHES:
            return this.getVertexFromReference((this.getJSON() as EventTreeMxGraphJSON)[ReferenceTypes.BRANCHES][outcomeJSON.name].outcome);
          default:
            throw new Error(`Unable to handle reference of type ${outcomeJSON.reference_type}`);
        }

      default:
        throw new Error(`Unable to parse outcome JSON: ${outcome}`);
    }
  }
}

export default PhoenixFlowchartMxGraphDecoder;
